import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import logo from "../Images/para.png";

pdfMake.vfs = pdfFonts.vfs;

// ──────────────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────────────
const formatDateLong = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = months[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mm}-${yy}`;
};

const formatCurrency = (amount) => {
  const val = Number(amount) || 0;
  return val.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

// Number to words (Indian) - simplified
const numberToWords = (num) => {
  if (num === 0) return "Zero";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? " " + inWords(n % 100) : "")
      );
    return "";
  };

  const n = Math.floor(num);
  let result = "";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;

  if (crore) result += inWords(crore) + " Crore ";
  if (lakh) result += inWords(lakh) + " Lakh ";
  if (thousand) result += inWords(thousand) + " Thousand ";
  if (rest) result += inWords(rest);

  return result.trim();
};

// ──────────────────────────────────────────────────────────────────────────
// SINGLE CUSTOM BILL PDF GENERATOR — COMPACT SINGLE PAGE
// ──────────────────────────────────────────────────────────────────────────
export const generateSingleCustomBillPDF = (bill) => {
  const items = bill.custom_items || bill.items || [];
  const ownerCompanyName = "PARAS ROADLINES";

  // ── Party details (single party) ──
  const partyName = bill.party_name || bill.party_text || "-";
  const partyAddress = bill.address || "-";
  const partyGst = bill.gst_no || "-";
  const partyPan = bill.pan_no || "-";
  const partyContact = bill.contact_no || "-";

  // ── Invoice details ──
  const invoiceNo = bill.invoice_no || `INV/${bill.billing_id || "-"}`;
  const ewayBillNo = bill.eway_bill_no || "-";
  const invoiceDate = formatDateLong(bill.invoice_date || bill.created_at);
  const deliveryNote = bill.delivery_note || "";
  const paymentTerms = bill.payment_terms || "";
  const refNo = bill.reference_no || invoiceNo;
  const refDate = formatDateLong(bill.reference_date || bill.invoice_date);
  const otherRefs = bill.other_references || "";
  const buyerOrderNo = bill.buyer_order_no || "";
  const buyerOrderDate = formatDateLong(bill.buyer_order_date);
  const dispatchDocNo = bill.dispatch_doc_no || "";
  const deliveryNoteDate = formatDateLong(bill.delivery_note_date);
  const dispatchedThrough = bill.dispatched_through || ownerCompanyName;
  const destination = bill.destination || "-";
  const billOfLading = bill.bill_of_lading || "-";
  const motorVehicleNo = bill.motor_vehicle_no || "-";
  const termsOfDelivery = bill.terms_of_delivery || "";

  // ── Tax calculations ──
  const totalTaxable = parseFloat(bill.total_taxable_value) || 0;
  const cgstPercent = parseFloat(bill.cgst_percent) || 0;
  const cgstAmount = parseFloat(bill.cgst_amount) || 0;
  const sgstPercent = parseFloat(bill.sgst_percent) || 0;
  const sgstAmount = parseFloat(bill.sgst_amount) || 0;
  const grandTotal =
    parseFloat(bill.grand_total) || totalTaxable + cgstAmount + sgstAmount;
  const totalTaxAmount = cgstAmount + sgstAmount;

  let totalQuantity = 0;
  let unitLabel = "";
  items.forEach((it) => {
    totalQuantity += parseFloat(it.quantity) || 0;
    if (!unitLabel && it.unit) unitLabel = it.unit;
  });

  // ═══════════════════════════════════════════════════════════════
  // TOP INFO TABLE — Compact, single party uses full space
  // ═══════════════════════════════════════════════════════════════
  const topInfoTable = {
    table: {
      widths: ["50%", "25%", "25%"],
      body: [
        // Row 1: Party (spans 4) | Invoice No | Dated
        [
          {
            stack: [
              {
                text: "Bill From / Bill To",
                fontSize: 7,
                color: "#666666",
                margin: [0, 0, 0, 1],
              },
              { text: partyName, bold: true, fontSize: 11 },
              { text: partyAddress, fontSize: 8.5, margin: [0, 1, 0, 0] },
              {
                text: `GSTIN/UIN : ${partyGst}`,
                fontSize: 8.5,
                margin: [0, 1, 0, 0],
              },
              {
                text: `PAN : ${partyPan}`,
                fontSize: 8.5,
                margin: [0, 1, 0, 0],
              },
              {
                text: `Contact : ${partyContact}`,
                fontSize: 8.5,
                margin: [0, 1, 0, 0],
              },
            ],
            rowSpan: 4,
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Invoice No.", fontSize: 7, color: "#666666" },
              {
                text: invoiceNo,
                bold: true,
                fontSize: 10,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Dated", fontSize: 7, color: "#666666" },
              {
                text: invoiceDate,
                bold: true,
                fontSize: 10,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
        // Row 2: (spanned) | E-way Bill | Delivery Note
        [
          {},
          {
            stack: [
              { text: "e-Way Bill No.", fontSize: 7, color: "#666666" },
              {
                text: ewayBillNo || " ",
                bold: true,
                fontSize: 9,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Delivery Note", fontSize: 7, color: "#666666" },
              { text: deliveryNote || " ", fontSize: 9, margin: [0, 1, 0, 0] },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
        // Row 3: (spanned) | Reference | Buyer Order
        [
          {},
          {
            stack: [
              { text: "Reference No. & Date", fontSize: 7, color: "#666666" },
              {
                text: `${refNo} dt. ${refDate}`,
                bold: true,
                fontSize: 8.5,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Buyer's Order No.", fontSize: 7, color: "#666666" },
              {
                text: buyerOrderNo || " ",
                bold: true,
                fontSize: 9,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
        // Row 4: (spanned) | Mode/Terms Payment | Order Date
        [
          {},
          {
            stack: [
              { text: "Mode/Terms of Payment", fontSize: 7, color: "#666666" },
              { text: paymentTerms || " ", fontSize: 9, margin: [0, 1, 0, 0] },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Order Date", fontSize: 7, color: "#666666" },
              {
                text: buyerOrderDate !== "-" ? buyerOrderDate : " ",
                bold: true,
                fontSize: 9,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
        // Row 5: Dispatched | Destination | Motor Vehicle
        [
          {
            stack: [
              { text: "Dispatched through", fontSize: 7, color: "#666666" },
              {
                text: dispatchedThrough,
                bold: true,
                fontSize: 9,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Destination", fontSize: 7, color: "#666666" },
              {
                text: destination,
                bold: true,
                fontSize: 9,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Motor Vehicle No.", fontSize: 7, color: "#666666" },
              {
                text: motorVehicleNo,
                bold: true,
                fontSize: 10,
                color: "#c00000",
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
        // Row 6: Bill of Lading | Dispatch Doc | Delivery Note Date
        [
          {
            stack: [
              {
                text: "Bill of Lading/LR-RR No.",
                fontSize: 7,
                color: "#666666",
              },
              { text: billOfLading, fontSize: 9, margin: [0, 1, 0, 0] },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Dispatch Doc No.", fontSize: 7, color: "#666666" },
              { text: dispatchDocNo || " ", fontSize: 9, margin: [0, 1, 0, 0] },
            ],
            margin: [4, 2, 4, 2],
          },
          {
            stack: [
              { text: "Delivery Note Date", fontSize: 7, color: "#666666" },
              {
                text: deliveryNoteDate !== "-" ? deliveryNoteDate : " ",
                fontSize: 9,
                margin: [0, 1, 0, 0],
              },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
        // Row 7: Terms of Delivery (spans 2) | Other References
        [
          {
            stack: [
              { text: "Terms of Delivery", fontSize: 7, color: "#666666" },
              {
                text: termsOfDelivery || " ",
                bold: true,
                fontSize: 8.5,
                margin: [0, 1, 0, 0],
              },
            ],
            colSpan: 2,
            margin: [4, 2, 4, 2],
          },
          {},
          {
            stack: [
              { text: "Other References", fontSize: 7, color: "#666666" },
              { text: otherRefs || " ", fontSize: 8.5, margin: [0, 1, 0, 0] },
            ],
            margin: [4, 2, 4, 2],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#333333",
      vLineColor: () => "#333333",
    },
  };

  // ═══════════════════════════════════════════════════════════════
  // ITEMS TABLE — Compact
  // ═══════════════════════════════════════════════════════════════
  const itemsHeader = [
    {
      text: "Sl\nNo.",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 3, 0, 3],
    },
    {
      text: "Description of Goods",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 6, 0, 3],
    },
    {
      text: "HSN/SAC",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 6, 0, 3],
    },
    {
      text: "Quantity",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 6, 0, 3],
    },
    {
      text: "Rate",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 6, 0, 3],
    },
    {
      text: "per",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 6, 0, 3],
    },
    {
      text: "Amount",
      bold: true,
      fontSize: 8.5,
      alignment: "center",
      fillColor: "#f0f0f0",
      margin: [0, 6, 0, 3],
    },
  ];

  const itemsBody = [itemsHeader];

  items.forEach((it, idx) => {
    const qty = parseFloat(it.quantity) || 0;
    const rate = parseFloat(it.rate) || 0;
    const amt = parseFloat(it.amount) || 0;

    itemsBody.push([
      { text: idx + 1, fontSize: 9, alignment: "center", margin: [0, 2, 0, 2] },
      {
        text: it.description || "-",
        bold: true,
        fontSize: 9.5,
        margin: [4, 2, 4, 2],
      },
      {
        text: it.hsn || "-",
        fontSize: 9,
        alignment: "center",
        margin: [0, 2, 0, 2],
      },
      {
        text: `${qty.toFixed(3)} ${it.unit || ""}`,
        bold: true,
        fontSize: 9,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
      {
        text: rate ? rate.toFixed(2) : "-",
        fontSize: 9,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
      {
        text: it.per || "-",
        fontSize: 9,
        alignment: "center",
        margin: [0, 2, 0, 2],
      },
      {
        text: formatCurrency(amt),
        bold: true,
        fontSize: 9.5,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
    ]);
  });

  if (cgstAmount > 0) {
    itemsBody.push([
      { text: "", border: [true, false, true, false] },
      {
        text: "CGST",
        italics: true,
        bold: true,
        fontSize: 9.5,
        alignment: "right",
        margin: [0, 1, 4, 1],
        border: [true, false, true, false],
      },
      { text: "", border: [true, false, true, false] },
      { text: "", border: [true, false, true, false] },
      {
        text: `${cgstPercent}%`,
        fontSize: 9,
        alignment: "right",
        margin: [0, 1, 4, 1],
        border: [true, false, true, false],
      },
      { text: "", border: [true, false, true, false] },
      {
        text: formatCurrency(cgstAmount),
        fontSize: 9.5,
        alignment: "right",
        margin: [0, 1, 4, 1],
        border: [true, false, true, false],
      },
    ]);
  }

  if (sgstAmount > 0) {
    itemsBody.push([
      { text: "", border: [true, false, true, false] },
      {
        text: "SGST",
        italics: true,
        bold: true,
        fontSize: 9.5,
        alignment: "right",
        margin: [0, 1, 4, 1],
        border: [true, false, true, false],
      },
      { text: "", border: [true, false, true, false] },
      { text: "", border: [true, false, true, false] },
      {
        text: `${sgstPercent}%`,
        fontSize: 9,
        alignment: "right",
        margin: [0, 1, 4, 1],
        border: [true, false, true, false],
      },
      { text: "", border: [true, false, true, false] },
      {
        text: formatCurrency(sgstAmount),
        fontSize: 9.5,
        alignment: "right",
        margin: [0, 1, 4, 1],
        border: [true, false, true, false],
      },
    ]);
  }

  // Minimal padding — only 2 rows max
  const taxRowsCount = (cgstAmount > 0 ? 1 : 0) + (sgstAmount > 0 ? 1 : 0);
  const usedRows = items.length + taxRowsCount;
  const targetRows = 10; // ← total rows to fill page
  const paddingRows = Math.max(0, targetRows - usedRows);

  for (let i = 0; i < paddingRows; i++) {
    itemsBody.push([
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
      { text: " ", border: [true, false, true, false], margin: [0, 2, 0, 2] },
    ]);
  }

  // Grand Total row — highlighted
  // Grand Total row — highlighted (compact)
  itemsBody.push([
    { text: "", fillColor: "#fef3c7" },
    {
      text: "Total",
      bold: true,
      fontSize: 10,
      alignment: "right",
      margin: [0, 3, 5, 3],
      fillColor: "#fef3c7",
    },
    { text: "", fillColor: "#fef3c7" },
    {
      text: `${totalQuantity.toFixed(3)} ${unitLabel}`,
      bold: true,
      fontSize: 9.5,
      alignment: "right",
      margin: [0, 3, 5, 3],
      fillColor: "#fef3c7",
    },
    { text: "", fillColor: "#fef3c7" },
    { text: "", fillColor: "#fef3c7" },
    {
      text: `₹ ${formatCurrency(grandTotal)}`,
      bold: true,
      fontSize: 11,
      alignment: "right",
      color: "#c00000",
      margin: [0, 3, 5, 3],
      fillColor: "#fef3c7",
    },
  ]);

  const itemsTable = {
    table: {
      headerRows: 1,
      widths: [22, "*", 55, 65, 55, 30, 75],
      body: itemsBody,
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#333333",
      vLineColor: () => "#333333",
    },
  };

  // ═══════════════════════════════════════════════════════════════
  // AMOUNT WORDS — Compact
  // ═══════════════════════════════════════════════════════════════
  const amountWordsBlock = {
    table: {
      widths: ["*", 70],
      body: [
        [
          {
            stack: [
              {
                text: "Amount Chargeable (in words)",
                fontSize: 7,
                color: "#666666",
              },
              {
                text: `INR ${numberToWords(grandTotal)} Only`,
                bold: true,
                fontSize: 10,
                color: "#c00000",
                margin: [0, 1, 0, 0],
              },
            ],
            fillColor: "#f9fafb",
            margin: [5, 3, 5, 3],
          },
          {
            text: "E. & O.E",
            italics: true,
            fontSize: 8.5,
            alignment: "right",
            fillColor: "#f9fafb",
            margin: [0, 8, 5, 0],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#333333",
      vLineColor: () => "#333333",
    },
  };

  // ═══════════════════════════════════════════════════════════════
  // HSN SUMMARY — Compact
  // ═══════════════════════════════════════════════════════════════
  const hsnSummaryHeader = [
    [
      {
        text: "HSN/SAC",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        rowSpan: 2,
        margin: [0, 7, 0, 3],
      },
      {
        text: "Taxable\nValue",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        rowSpan: 2,
        margin: [0, 4, 0, 3],
      },
      {
        text: "CGST",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        colSpan: 2,
        margin: [0, 2, 0, 2],
      },
      {},
      {
        text: "SGST/UTGST",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        colSpan: 2,
        margin: [0, 2, 0, 2],
      },
      {},
      {
        text: "Total\nTax Amount",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        rowSpan: 2,
        margin: [0, 4, 0, 3],
      },
    ],
    [
      {},
      {},
      {
        text: "Rate",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        margin: [0, 2, 0, 2],
      },
      {
        text: "Amount",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        margin: [0, 2, 0, 2],
      },
      {
        text: "Rate",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        margin: [0, 2, 0, 2],
      },
      {
        text: "Amount",
        bold: true,
        fontSize: 8.5,
        alignment: "center",
        fillColor: "#f0f0f0",
        margin: [0, 2, 0, 2],
      },
      {},
    ],
  ];

  const hsnSummaryBody = [...hsnSummaryHeader];

  const hsnGroups = {};
  items.forEach((it) => {
    const hsn = it.hsn || "-";
    if (!hsnGroups[hsn]) hsnGroups[hsn] = { taxable: 0 };
    hsnGroups[hsn].taxable += parseFloat(it.amount) || 0;
  });

  Object.entries(hsnGroups).forEach(([hsn, data]) => {
    const cgstAmt = (data.taxable * cgstPercent) / 100;
    const sgstAmt = (data.taxable * sgstPercent) / 100;
    hsnSummaryBody.push([
      { text: hsn, fontSize: 9, alignment: "center", margin: [0, 2, 0, 2] },
      {
        text: formatCurrency(data.taxable),
        fontSize: 9,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
      {
        text: `${cgstPercent}%`,
        fontSize: 9,
        alignment: "center",
        margin: [0, 2, 0, 2],
      },
      {
        text: formatCurrency(cgstAmt),
        fontSize: 9,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
      {
        text: `${sgstPercent}%`,
        fontSize: 9,
        alignment: "center",
        margin: [0, 2, 0, 2],
      },
      {
        text: formatCurrency(sgstAmt),
        fontSize: 9,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
      {
        text: formatCurrency(cgstAmt + sgstAmt),
        fontSize: 9,
        alignment: "right",
        margin: [0, 2, 4, 2],
      },
    ]);
  });

  hsnSummaryBody.push([
    {
      text: "Total",
      bold: true,
      fontSize: 9.5,
      alignment: "right",
      fillColor: "#fef3c7",
      margin: [0, 3, 4, 3],
    },
    {
      text: formatCurrency(totalTaxable),
      bold: true,
      fontSize: 9.5,
      alignment: "right",
      fillColor: "#fef3c7",
      margin: [0, 3, 4, 3],
    },
    { text: "", fillColor: "#fef3c7" },
    {
      text: formatCurrency(cgstAmount),
      bold: true,
      fontSize: 9.5,
      alignment: "right",
      color: "#c00000",
      fillColor: "#fef3c7",
      margin: [0, 3, 4, 3],
    },
    { text: "", fillColor: "#fef3c7" },
    {
      text: formatCurrency(sgstAmount),
      bold: true,
      fontSize: 9.5,
      alignment: "right",
      color: "#c00000",
      fillColor: "#fef3c7",
      margin: [0, 3, 4, 3],
    },
    {
      text: formatCurrency(totalTaxAmount),
      bold: true,
      fontSize: 10,
      alignment: "right",
      color: "#c00000",
      fillColor: "#fef3c7",
      margin: [0, 3, 4, 3],
    },
  ]);

  const hsnSummaryTable = {
    table: {
      headerRows: 2,
      widths: ["*", 75, 40, 65, 40, 65, 70],
      body: hsnSummaryBody,
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#333333",
      vLineColor: () => "#333333",
    },
  };

  // ═══════════════════════════════════════════════════════════════
  // TAX WORDS + REMARKS — Compact
  // ═══════════════════════════════════════════════════════════════
  const taxWordsBlock = {
    table: {
      widths: ["*"],
      body: [
        [
          {
            stack: [
              {
                text: [
                  {
                    text: "Tax Amount (in words) : ",
                    fontSize: 8.5,
                    color: "#666666",
                  },
                  {
                    text: `INR ${numberToWords(totalTaxAmount)} Only`,
                    bold: true,
                    fontSize: 10,
                    color: "#c00000",
                  },
                ],
                margin: [0, 1, 0, 2],
              },
              {
                text: [
                  {
                    text: "Remarks : ",
                    italics: true,
                    fontSize: 8.5,
                    color: "#666666",
                  },
                  { text: `INVOICE NO.${invoiceNo}`, bold: true, fontSize: 9 },
                ],
                margin: [0, 0, 0, 1],
              },
            ],
            margin: [5, 3, 5, 3],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#333333",
      vLineColor: () => "#333333",
    },
  };

  // ═══════════════════════════════════════════════════════════════
  // DECLARATION + SIGNATURE — Compact
  // ═══════════════════════════════════════════════════════════════
  const declarationBlock = {
    table: {
      widths: ["55%", "45%"],
      body: [
        [
          {
            stack: [
              {
                text: "Declaration",
                italics: true,
                fontSize: 8.5,
                margin: [0, 0, 0, 1],
              },
              {
                text: "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.",
                fontSize: 8.5,
              },
              {
                text: "SUBJECT TO WARDHA JURISDICTION",
                fontSize: 8,
                bold: true,
                margin: [0, 6, 0, 0],
                color: "#666666",
              },
            ],
            margin: [5, 5, 5, 8],
          },
          {
            stack: [
              {
                text: `for ${partyName}`,
                bold: true,
                fontSize: 10,
                alignment: "right",
                margin: [0, 2, 5, 25], // ← reduced to 25px
              },

              {
                text: "Authorised Signatory",
                fontSize: 10,
                alignment: "right",
                bold: true,
                margin: [0, 0, 5, 3],
              },
            ],
            margin: [5, 5, 5, 5],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#333333",
      vLineColor: () => "#333333",
    },
  };
  const ownerCompanyGst = "27AAICV3639B12C";

  // ═══════════════════════════════════════════════════════════════
  // DOCUMENT DEFINITION — Minimal margins, no footer
  // ═══════════════════════════════════════════════════════════════
  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [12, 12, 12, 12], // ← Minimal margins
    defaultStyle: { fontSize: 9, font: "Roboto", color: "#000000" },
    images: { logo: logo },
    // ❌ NO FOOTER — removed completely
    content: [
      // Title
      {
        text: "CUSTOM BILL RECIEPT",
        bold: true,
        fontSize: 14,
        alignment: "center",
        color: "#111",
        margin: [0, 0, 0, 4],
      },

      // Company Header
      {
        columns: [
          {
            width: 75,
            stack: [{ image: "logo", width: 65, height: 65 }],
          },
          {
            width: "*",
            stack: [
              {
                text: "Subject to BHILAI jurisdiction",
                fontSize: 8,
                italics: true,
                alignment: "right",
                color: "#666666",
              },
              {
                text: ownerCompanyName,
                bold: true,
                fontSize: 18,
                alignment: "right",
                color: "#c00000",
                margin: [0, 1, 0, 1],
              },
              {
                text: "(FLEET OWNER & TRANSPORTING CONTRACTORS)",
                bold: true,
                fontSize: 9,
                alignment: "right",
              },
              {
                text: "52, R.S.S. Market, Power House, Bhilai - 490011 (C.G.)",
                fontSize: 8.5,
                alignment: "right",
                margin: [0, 1, 0, 1],
              },
              {
                text: [
                  { text: "MOB", bold: true, fontSize: 8.5 },
                  {
                    text: " : 7974125386, 7999552398, 9425234581",
                    fontSize: 8.5,
                  },
                ],
                alignment: "right",
              },
            ],
          },
        ],
        margin: [0, 0, 0, 3],
      },

      // GSTIN Info — Simple line (like before)
      {
        stack: [
          {
            text: [
              { text: "GSTIN NO. : ", bold: true, fontSize: 8.5 },
              { text: ownerCompanyGst, fontSize: 8.5 },
            ],
          },
          {
            text: [
              { text: "DAILY SERVICE : ", bold: true, fontSize: 8.5 },
              { text: "ALL OVER INDIA", fontSize: 8.5 },
            ],
          },
        ],
        margin: [0, 0, 0, 2],
      },

      // Divider Line
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: 570,
            y2: 0,
            lineWidth: 0.8,
            lineColor: "#000000",
          },
        ],
        margin: [0, 0, 0, 4],
      },

      // Main sections — no extra spacing
      topInfoTable,
      itemsTable,
      amountWordsBlock,
      hsnSummaryTable,
      taxWordsBlock,
      declarationBlock,
    ],
  };

  return docDefinition;
};

// ──────────────────────────────────────────────────────────────────────────
// MULTI-BILL WRAPPER
// ──────────────────────────────────────────────────────────────────────────
export const generateCustomBillPDF = (billData) => {
  if (!Array.isArray(billData)) billData = [billData];

  const combinedContent = [];
  billData.forEach((bill, idx) => {
    const singleDoc = generateSingleCustomBillPDF(bill);
    if (idx > 0) combinedContent.push({ text: "", pageBreak: "before" });
    combinedContent.push(...singleDoc.content);
  });

  return {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [12, 12, 12, 12],
    defaultStyle: { fontSize: 9, font: "Roboto", color: "#000000" },
    images: { logo: logo },
    // ❌ NO FOOTER
    content: combinedContent,
  };
};

// ──────────────────────────────────────────────────────────────────────────
// EXPORT FUNCTIONS
// ──────────────────────────────────────────────────────────────────────────
export const printSingleCustomBill = (bill) => {
  if (!bill) return alert("No bill data to print");
  const doc = generateSingleCustomBillPDF(bill);
  pdfMake.createPdf(doc).open();
};

export const downloadSingleCustomBill = (bill) => {
  if (!bill) return alert("No bill data to download");
  const doc = generateSingleCustomBillPDF(bill);
  const filename = `TaxInvoice_${bill.invoice_no || bill.billing_id || "Bill"}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdfMake.createPdf(doc).download(filename);
};

export const printCustomBillReport = (billData) => {
  if (!billData || billData.length === 0) return alert("No data to print");
  const doc = generateCustomBillPDF(billData);
  pdfMake.createPdf(doc).open();
};

export const downloadCustomBillReport = (billData) => {
  if (!billData || billData.length === 0) return alert("No data to download");
  const doc = generateCustomBillPDF(billData);
  const filename = `TaxInvoice_Bills_${new Date().toISOString().split("T")[0]}.pdf`;
  pdfMake.createPdf(doc).download(filename);
};
